import { useState, useMemo } from 'react'

/**
 * @typedef {Object} PaginationHandlers
 * @property {function} setPageIndex
 * @property {function} resetPagination
 */

/**
 * @typedef {Object} PaginationHook
 * @property {number} resultsCount Results count from where pagination derives
 * @property {number} pageIndex Current page index
 * @property {number} pageNumber Current page number
 * @property {number} startResult Current page start result index
 * @property {number} endResult Current page end result index
 * @property {PaginationHandlers} handlers
 */

/**
 * @typedef {Object} PaginationHookProps
 * @property {number} pageSize
 * @property {number} resultsCount
 * @property {number=} defaultPage
 */

/**
 * Calculate pagination from specific results count and provide state handlers.
 * @param {PaginationHookProps} props
 * @return {PaginationHook}
 */
const usePaginationHook = ({ pageSize = 0, resultsCount = 0, defaultPage = 0 }) => {
  const [pageIndex, setPageIndex] = useState(defaultPage)

  const resetPagination = () => {
    setPageIndex(0)
  }

  const pageMeta = useMemo(() => {
    const pageNumber = pageIndex + 1
    const startResult = pageIndex * pageSize + 1
    const endResult = Math.min(startResult + pageSize - 1, resultsCount)

    return {
      pageNumber,
      startResult,
      endResult,
    }
  }, [pageIndex, resultsCount])

  return {
    resultsCount,
    pageIndex,
    ...pageMeta,
    handlers: {
      setPageIndex,
      resetPagination,
    },
  }
}

export default usePaginationHook
